import React from "react"
import { useTranslation } from "react-i18next"
import { Link, NavLink, useNavigate } from "react-router"

import { CopyIconButton } from "@/components/copy-icon-button"
import { Page404 } from "@/components/nav/page404"
import { PageCard } from "@/components/page-card/page-card"
import { PageTitle } from "@/components/page-title"
import { useActiveOrganizationId, useAuthentication } from "@/contexts"
import { AdminEntityFieldsSkeleton } from "@/modules/admin/components/admin-entity-fields-skeleton"
import {
  CredentialIdentityReadonlyFields,
  CredentialPasswordRevealField,
  CredentialWebsiteLink,
  useAdminCredentialPageState,
  useConfirmDeleteCredential,
} from "@/modules/admin/credentials"
import { adminEntityFieldsMaxWidthSx } from "@/modules/admin/utils/admin-entity-fields-max-width-sx"
import { ReadonlyField } from "@/nzds/forms/readonly-field/readonly-field"
import { useUrl } from "@/utils/url"
import { getDisplayValue } from "@/utils/value"

import { Box, Button, Link as MuiLink, Stack } from "@mui/material"

export const AdminCredentialRoute: React.FC = () => {
  const { activeOrganizationId } = useActiveOrganizationId()
  const navigate = useNavigate()
  const { t } = useTranslation("common")
  const { canManageCredentials } = useAuthentication()
  const { adminCredentialEditPath, adminCredentialsPath } = useUrl()
  const {
    credentialData,
    credentialError,
    credentialId,
    credentialIsLoading,
    credentialIsUnavailable,
  } = useAdminCredentialPageState()

  const organizationId = activeOrganizationId ?? ""

  const { confirmAndDeleteCredential, deleteCredentialIsPending } =
    useConfirmDeleteCredential({
      organizationId,
      onSuccess: () => {
        void navigate(adminCredentialsPath, { replace: true })
      },
    })

  const handleDeleteClick = () => {
    if (!credentialData || !canManageCredentials) {
      return
    }

    confirmAndDeleteCredential({
      id: credentialData.id,
      providerName: credentialData.providerName,
    }).catch(() => undefined)
  }

  if (!organizationId) {
    return null
  }

  if (credentialError && !credentialData) {
    throw credentialError
  }

  if (credentialIsUnavailable) {
    return <Page404 />
  }

  return (
    <Stack sx={{ gap: 2 }}>
      <PageTitle
        breadcrumbActions={
          <MuiLink component={Link} to={adminCredentialsPath}>
            {t("admin.credentials")}
          </MuiLink>
        }
        heading={t("admin.credentialDetail.title")}
        primaryActions={
          canManageCredentials && credentialId !== undefined ? (
            <Button
              component={NavLink}
              to={adminCredentialEditPath(credentialId)}
              variant="contained"
            >
              {t("button.edit")}
            </Button>
          ) : undefined
        }
        secondaryActions={
          canManageCredentials ? (
            <Button
              color="error"
              disabled={
                deleteCredentialIsPending ||
                credentialIsLoading ||
                !credentialData
              }
              onClick={handleDeleteClick}
            >
              {t("button.delete")}
            </Button>
          ) : undefined
        }
      />
      <PageCard>
        {credentialIsLoading || !credentialData ? (
          <AdminEntityFieldsSkeleton
            ariaLabel={t("loadingAriaLabel", {
              title: t("admin.credentialDetail.title"),
            })}
          />
        ) : (
          <Stack sx={{ ...adminEntityFieldsMaxWidthSx, gap: 2 }}>
            <CredentialIdentityReadonlyFields
              providerName={credentialData.providerName}
              resources={credentialData.resources}
            />
            <ReadonlyField
              label={t("admin.credentialsUi.websiteLabel")}
              value={<CredentialWebsiteLink website={credentialData.website} />}
            />
            <ReadonlyField
              label={t("admin.credentialsUi.usernameLabel")}
              value={
                <Stack
                  direction="row"
                  sx={{ alignItems: "center", gap: 0.5, minHeight: 40 }}
                >
                  <Box component="span">
                    {getDisplayValue(credentialData.username)}
                  </Box>
                  <CopyIconButton
                    ariaLabel={t("admin.credentialsUi.copyUsernameAriaLabel")}
                    value={credentialData.username}
                  />
                </Stack>
              }
            />
            {canManageCredentials ? (
              <CredentialPasswordRevealField
                key={credentialData.id}
                credentialId={credentialData.id}
                organizationId={organizationId}
              />
            ) : null}
            {credentialData.notes?.trim() ? (
              <ReadonlyField
                label={t("admin.credentialsUi.notesLabel")}
                value={
                  <Box component="pre" sx={{ m: 0, whiteSpace: "pre-wrap" }}>
                    {credentialData.notes}
                  </Box>
                }
              />
            ) : null}
          </Stack>
        )}
      </PageCard>
    </Stack>
  )
}
